import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

export interface VitalSign {
  id?: number;
  patient_id: number;
  user_id?: number;
  appointment_id?: number;
  medical_record_id?: number;
  weight?: number;
  height?: number;
  temperature?: number;
  heart_rate?: number;
  respiratory_rate?: number;
  blood_pressure_systolic?: number;
  blood_pressure_diastolic?: number;
  oxygen_saturation?: number;
  abdominal_perimeter?: number;
  waist_hip_ratio?: number;
  extras?: any;
  created_at?: string;
  updated_at?: string;
  imc?: number;
  blood_pressure?: string;
  is_weight_normal?: boolean;
  is_temperature_normal?: boolean;
  is_blood_pressure_normal?: boolean;
}

export interface VitalSignsListResponse {
  success: boolean;
  message: string;
  data: VitalSign[];
  meta?: {
    current_page: number;
    total: number;
    per_page: number;
    last_page: number;
  };
}

@Injectable({ providedIn: 'root' })
export class VitalSignsService {
  private apiUrl = `${environment.apiUrl}/vital-signs`;

  constructor(private http: HttpClient) {}

  /**
   * Crea nuevos signos vitales
   */
  create(data: Partial<VitalSign>): Observable<VitalSign> {
    return this.http.post<any>(this.apiUrl, data)
      .pipe(
        map(response => response.data),
        catchError(this.handleError)
      );
  }

  /**
   * Actualiza signos vitales existentes
   */
  update(id: number, data: Partial<VitalSign>): Observable<VitalSign> {
    return this.http.put<any>(`${this.apiUrl}/${id}`, data)
      .pipe(
        map(response => response.data),
        catchError(this.handleError)
      );
  }

  /**
   * Obtiene un registro específico de signos vitales
   */
  getById(id: number): Observable<VitalSign> {
    return this.http.get<any>(`${this.apiUrl}/${id}`)
      .pipe(
        map(response => response.data),
        catchError(this.handleError)
      );
  }

  /**
   * Elimina un registro de signos vitales
   */
  delete(id: number): Observable<any> {
    return this.http.delete<any>(`${this.apiUrl}/${id}`)
      .pipe(
        map(response => response),
        catchError(this.handleError)
      );
  }

  /**
   * Obtiene histórico paginado de signos vitales del paciente
   */
  getHistorico(patientId: number, params?: { start_date?: string; end_date?: string; per_page?: number }): Observable<VitalSignsListResponse> {
    let httpParams = new HttpParams();
    if (params?.start_date) httpParams = httpParams.set('start_date', params.start_date);
    if (params?.end_date) httpParams = httpParams.set('end_date', params.end_date);
    if (params?.per_page) httpParams = httpParams.set('per_page', params.per_page.toString());

    return this.http.get<any>(`${environment.apiUrl}/patients/${patientId}/vital-signs`, { params: httpParams })
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * Obtiene los signos vitales más recientes del paciente
   */
  getLatest(patientId: number): Observable<VitalSign | null> {
    return this.http.get<any>(`${environment.apiUrl}/patients/${patientId}/vital-signs/latest`)
      .pipe(
        map(response => response.data),
        catchError(this.handleError)
      );
  }

  /**
   * Manejo centralizado de errores
   */
  private handleError(error: any): Observable<never> {
    let errorMessage = 'Ocurrió un error en el servidor';

    if (error.error?.message) {
      errorMessage = error.error.message;
    } else if (error.status === 0) {
      errorMessage = 'No se pudo conectar con el servidor';
    } else if (error.status === 401) {
      errorMessage = 'Sesión expirada, por favor inicia sesión nuevamente';
    } else if (error.status === 404) {
      errorMessage = 'Recurso no encontrado';
    }

    console.error('[VitalSignsService] Error:', errorMessage, error);
    return throwError(() => new Error(errorMessage));
  }
}
